import React, { useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './ExportModal.css';

const ExportModal = ({ messages, topic, onClose, getSpeakerInfo }) => {
  const [exportFormat, setExportFormat] = useState('txt');
  const [isExporting, setIsExporting] = useState(false);

  const formatMessage = (message, speaker) => {
    const timestamp = formatTime(message.timestamp);
    return `[${timestamp}] ${speaker.name}: ${message.content}`;
  };

  const formatTime = (timestamp) => {
    const minutes = Math.floor(timestamp / 60);
    const period = Math.floor(minutes / 60) >= 12 ? 'PM' : 'AM';
    const displayHours = Math.floor(minutes / 60) % 12 || 12;
    const displayMinutes = minutes % 60;
    
    return `${displayHours}:${displayMinutes.toString().padStart(2, '0')} ${period}`;
  };

  const exportAsText = () => {
    const header = `Multi-AI Chat Demo - Transcript\n` +
                  `Topic: ${topic.title}\n` +
                  `Description: ${topic.description}\n` +
                  `Export Date: ${new Date().toLocaleString()}\n` +
                  `${'='.repeat(50)}\n\n`;
    
    const content = messages.map(message => {
      const speaker = getSpeakerInfo(message.speaker);
      return formatMessage(message, speaker);
    }).join('\n\n');
    
    const fullContent = header + content;
    
    const blob = new Blob([fullContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-transcript-${topic.id}-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportAsJSON = () => {
    const exportData = {
      topic: {
        id: topic.id,
        title: topic.title,
        description: topic.description
      },
      exportDate: new Date().toISOString(),
      messages: messages.map(message => {
        const speaker = getSpeakerInfo(message.speaker);
        return {
          id: message.id,
          speaker: {
            id: speaker.id,
            name: speaker.name,
            title: speaker.title
          },
          timestamp: message.timestamp,
          content: message.content,
          isUser: message.isUser || message.speaker === 'user'
        };
      })
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-transcript-${topic.id}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportAsMarkdown = () => {
    const header = `# Multi-AI Chat Demo - Transcript\n\n` +
                  `**Topic:** ${topic.title}\n\n` +
                  `**Description:** ${topic.description}\n\n` +
                  `**Export Date:** ${new Date().toLocaleString()}\n\n` +
                  `---\n\n`;
    
    const content = messages.map(message => {
      const speaker = getSpeakerInfo(message.speaker);
      const timestamp = formatTime(message.timestamp);
      
      return `## ${timestamp} - ${speaker.name}\n\n${message.content}\n`;
    }).join('\n');
    
    const fullContent = header + content;
    
    const blob = new Blob([fullContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-transcript-${topic.id}-${Date.now()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportAsPDF = async () => {
    setIsExporting(true);
    
    try {
      // Create a temporary element to render the content
      const tempDiv = document.createElement('div');
      tempDiv.style.cssText = `
        position: absolute;
        left: -9999px;
        top: -9999px;
        width: 800px;
        padding: 40px;
        background: white;
        color: black;
        font-family: Arial, sans-serif;
        line-height: 1.6;
      `;
      
      // Build HTML content
      const header = `
        <h1 style="color: #4FC3F7; margin-bottom: 20px;">Multi-AI Chat Demo - Transcript</h1>
        <div style="margin-bottom: 30px;">
          <h2 style="color: #333; margin-bottom: 10px;">${topic.title}</h2>
          <p style="color: #666; margin-bottom: 5px;">${topic.description}</p>
          <p style="color: #888; font-size: 14px;">Export Date: ${new Date().toLocaleString()}</p>
        </div>
        <hr style="border: 1px solid #eee; margin: 30px 0;">
      `;
      
    const messagesHtml = messages.map(message => {
      const speaker = getSpeakerInfo(message.speaker);
      const timestamp = formatTime(message.timestamp);
      
      return `
        <div style="margin-bottom: 25px;">
          <h3 style="color: ${speaker.color}; margin-bottom: 8px;">
            ${timestamp} - ${speaker.name}
          </h3>
          <p style="color: #333; margin-left: 20px;">${message.content}</p>
        </div>
      `;
    }).join('');
      
      tempDiv.innerHTML = header + messagesHtml;
      document.body.appendChild(tempDiv);
      
      // Generate PDF
      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        allowTaint: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save(`chat-transcript-${topic.id}-${Date.now()}.pdf`);
      
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Error generating PDF. Please try another format.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      switch (exportFormat) {
        case 'txt':
          exportAsText();
          break;
        case 'json':
          exportAsJSON();
          break;
        case 'md':
          exportAsMarkdown();
          break;
        case 'pdf':
          await exportAsPDF();
          break;
        default:
          throw new Error('Unknown export format');
      }
      
      setTimeout(() => {
        setIsExporting(false);
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
      setIsExporting(false);
    }
  };

  return (
    <div className="export-modal-overlay">
      <div className="export-modal">
        <div className="export-modal-header">
          <h2>Export Transcript</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>
        
        <div className="export-modal-content">
          <div className="export-info">
            <h3>{topic.title}</h3>
            <p>{topic.description}</p>
            <p className="message-count">{messages.length} messages</p>
          </div>
          
          <div className="format-selection">
            <h4>Export Format:</h4>
            <div className="format-options">
              <label className="format-option">
                <input
                  type="radio"
                  value="txt"
                  checked={exportFormat === 'txt'}
                  onChange={(e) => setExportFormat(e.target.value)}
                />
                <span>Plain Text (.txt)</span>
              </label>
              
              <label className="format-option">
                <input
                  type="radio"
                  value="json"
                  checked={exportFormat === 'json'}
                  onChange={(e) => setExportFormat(e.target.value)}
                />
                <span>JSON (.json)</span>
              </label>
              
              <label className="format-option">
                <input
                  type="radio"
                  value="md"
                  checked={exportFormat === 'md'}
                  onChange={(e) => setExportFormat(e.target.value)}
                />
                <span>Markdown (.md)</span>
              </label>
              
              <label className="format-option">
                <input
                  type="radio"
                  value="pdf"
                  checked={exportFormat === 'pdf'}
                  onChange={(e) => setExportFormat(e.target.value)}
                />
                <span>PDF (.pdf)</span>
              </label>
            </div>
          </div>
        </div>
        
        <div className="export-modal-footer">
          <button 
            className="cancel-button" 
            onClick={onClose}
            disabled={isExporting}
          >
            Cancel
          </button>
          <button 
            className="export-button" 
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
